import React, { useMemo, useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { createGTMTracking, getGTMAttributes } from '../../utils/gtm/gtm-tracking';
import { cn } from '../../utils/cn';
import { getInterviewTabs } from '../landing/landingContent';
import { FreeTrialButton } from './FreeTrialButton';
import {
  VoiceAnalysisContent,
  FaceAnalysisContent,
  CodeGraphContent,
  AllAnalysisContent
} from '../landing/AnalysisContent';

export const InterviewSectionV2: React.FC = () => {
  const { t } = useTranslation('marketing');
  const tabs = useMemo(() => getInterviewTabs(t), [t]);
  const [activeTab, setActiveTab] = useState("voice");
  const [isSticky, setIsSticky] = useState(false);
  const [isUserClicking, setIsUserClicking] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const activeTabData = tabs.find(tab => tab.id === activeTab);

  const activeTabIndex = tabs.findIndex(tab => tab.id === activeTab);
  const isFirstTab = activeTabIndex === 0;
  const absolutePosition = (isFirstTab || activeTabIndex < tabs.length / 2) ? 'top-0' : 'bottom-0';

  // Detect mobile (below lg breakpoint) to reduce scroll distance per tab
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 1023px)');
    setIsMobile(mql.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // Framer-style sticky section with scroll progress
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          if (!containerRef.current || !sectionRef.current) {
            ticking = false;
            return;
          }

          const containerRect = containerRef.current.getBoundingClientRect();
          const shouldBeSticky = containerRect.top <= 0 && containerRect.bottom > window.innerHeight;
          setIsSticky(shouldBeSticky);

          if (shouldBeSticky) {
            const containerHeight = containerRef.current.offsetHeight;
            const viewportHeight = window.innerHeight;
            const scrollableHeight = containerHeight - viewportHeight;
            const scrolled = Math.abs(containerRect.top);
            const progress = Math.min(scrolled / scrollableHeight, 1);

            if (!isUserClicking) {
              const tabCount = tabs.length;
              const tabIndex = Math.floor(progress * tabCount);
              const clampedIndex = Math.min(Math.max(tabIndex, 0), tabCount - 1);

              if (tabs[clampedIndex] && tabs[clampedIndex].id !== activeTab) {
                setActiveTab(tabs[clampedIndex].id);
              }
            }
          }

          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [activeTab, isUserClicking]);

  const handleTabClick = (tabId: string) => {
    setIsUserClicking(true);
    setActiveTab(tabId);

    const tabIndex = tabs.findIndex(tab => tab.id === tabId);
    if (tabIndex !== -1 && containerRef.current) {
      const containerHeight = containerRef.current.offsetHeight;
      const viewportHeight = window.innerHeight;
      const scrollableHeight = containerHeight - viewportHeight;

      let targetScrollPosition;
      if (tabIndex === 0) {
        targetScrollPosition = 1;
      } else {
        targetScrollPosition = (tabIndex / (tabs.length - 1)) * scrollableHeight;
      }

      window.scrollTo({
        top: containerRef.current.offsetTop + targetScrollPosition,
        behavior: 'smooth'
      });
    }

    setTimeout(() => {
      setIsUserClicking(false);
    }, 1500);
  };

  const getContentComponent = (contentId: string) => {
    switch (contentId) {
      case 'voice-analysis':
        return <VoiceAnalysisContent />;
      case 'face-analysis':
        return <FaceAnalysisContent />;
      case 'code-graph':
        return <CodeGraphContent />;
      case 'all-analysis':
        return <AllAnalysisContent />;
      default:
        return null;
    }
  };

  return (
    <div
      ref={containerRef}
      className="w-full min-w-screen my-20"
      style={{
        height: `${tabs.length * (isMobile ? 35 : 50)}vh`,
        position: 'relative'
      }}
    >
      <section
        ref={sectionRef}
        className={`w-full w-screen bg-white px-4 sm:px-6 md:px-8 lg:px-12 xl:px-20 ${isSticky ? 'fixed top-0 z-50' : `absolute ${absolutePosition}`}`}
        {...getGTMAttributes(createGTMTracking('SECTION_VIEWED', { origin: 'welcome_page', target: 'interview' }))}
        style={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}
      >
        <div>
          <div className="flex flex-col py-8 sm:py-12 md:py-16 lg:py-20 xl:py-24 2xl:py-32 gap-6 sm:gap-8 md:gap-10 lg:gap-12">
            <motion.header
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.05 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="flex flex-row justify-between items-start sm:items-center w-full gap-4 sm:gap-6"
            >
              <div className="flex flex-col gap-2 sm:gap-3 md:gap-4 lg:gap-5">
                <h1 className="text-[#1B1B28] text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold">
                  {t('interview_v2.title')}
                </h1>
                {isSticky && (
                  <div className=" hidden flex items-center gap-3 text-sm text-gray-600">
                    <span>{t('interview_v2.scroll_hint')}</span>
                    <div className="flex gap-1">
                      {tabs.map((tab, index) => {
                        const currentIndex = tabs.findIndex(t => t.id === activeTab);
                        return (
                          <div
                            key={tab.id}
                            className={`w-2 h-2 rounded-full transition-all duration-500 ${activeTab === tab.id
                                ? 'bg-[#171D27] scale-125'
                                : index < currentIndex
                                  ? 'bg-gray-400'
                                  : 'bg-gray-300'
                              }`}
                          />
                        );
                      })}
                    </div>
                    <span className="text-xs text-gray-500">
                      {tabs.findIndex(t => t.id === activeTab) + 1} of {tabs.length}
                    </span>
                  </div>
                )}
              </div>
              {/* V2: Free Trial button instead of Book a Demo */}
              <FreeTrialButton origin="interview_section" />
            </motion.header>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.05 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="flex flex-col lg:flex-row items-center gap-6 sm:gap-8 w-full"
            >
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.05 }}
                transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
                className="w-full lg bg-white rounded-2xl p-4 sm:p-6 md:p-8 lg:p-10"
              >
                <div className="space-y-6 sm:space-y-8 md:space-y-10 lg:space-y-12">
                  {tabs.map((tab) => (
                    <div key={tab.id} className="border-b-2 border-[#E1D2DA]">
                      <button
                        onClick={() => handleTabClick(tab.id)}
                        className={cn(
                          "w-full text-left group transition-all duration-200",
                          activeTab === tab.id ? "opacity-100" : "opacity-50 hover:opacity-75"
                        )}
                        {...getGTMAttributes(createGTMTracking('SWITCH_INTERVIEW_TAB', { origin: 'interview_section', target: tab.id }))}
                      >
                        {activeTab === tab.id ? (
                          <div className="space-y-2 sm:space-y-3">
                            <div className="flex items-center justify-between">
                              <h3 className="font-semibold text-xl sm:text-2xl lg:text-[30px] leading-tight sm:leading-[34px] tracking-[-0.12px] text-invirtus-dark">
                                {tab.title}
                              </h3>
                              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-dark-invirtus flex-shrink-0" />
                            </div>
                            <p className="text-sm text-dark-invirtus/70">
                              {tab.description}
                            </p>
                            <div className="h-0.5 bg-line mt-4 sm:mt-6" />
                          </div>
                        ) : (
                          <div className="space-y-4 sm:space-y-6">
                            <h3 className="font-semibold text-xl sm:text-2xl lg:text-[30px] leading-tight sm:leading-[34px] tracking-[-0.12px] text-invirtus-dark">
                              {tab.title}
                            </h3>
                            <div className="h-0.5 bg-line" />
                          </div>
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                ref={contentRef}
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.05 }}
                transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
                className="w-full max-w-3xl lg:flex-shrink-0"
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, y: 20, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -20, scale: 0.98 }}
                    transition={{
                      duration: 0.5,
                      ease: "easeOut"
                    }}
                  >
                    {activeTabData && getContentComponent(activeTabData.contentId)}
                  </motion.div>
                </AnimatePresence>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};
